import {
    Alert,
    Button,
    Grid,
    styled,
    ToggleButton,
    ToggleButtonGroup,
    Switch,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';

import { shiftApi } from 'api';
import { EmployeeEntity } from 'api/generated';
import AppModal from 'components/AppModal';
import { H2 } from 'components/Typography';
import CalendarInput from 'components/input-fields/CalendarInput';
import { useSeason } from 'contexts/SeasonContext';
import { addDays, format } from 'date-fns';
import { useFormik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEndDateValid, isValidDate } from 'utils/dateValidator';
import * as Yup from 'yup';
import ToBePublishedAccordion from './toBePublishedAccordion';
import { checkTokenRole } from 'utils/checkToken';
import whatsappLogo from 'assets/images/whatsapp.png';


interface ModalProps {
    open: boolean;
    department: { id: number, label: string };
    onClose: () => void;
    onSubmit: (startDate: Date, endDate: Date, mode: string, sendOnWhatsapp: boolean, languageOverride?: string) => void;
    defaultStartDate?: Date;
    defaultEndDate?: Date;
}


const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 700,
    minWidth: 300,

    [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

// Updated custom styled switch
const WhatsAppSwitch = styled(Switch)(({ theme }) => ({
  width: 58,
  height: 34,
  padding: 7,
  display: 'flex',
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(24px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#25D366',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundImage: `url(${whatsappLogo})`,
    backgroundSize: '70%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: 26,
    height: 26,
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 17,
    backgroundColor: theme.palette.grey[400],
    opacity: 1,
    transition: 'background-color 300ms',
  },
}));


const PublishShiftsDatePicker: FC<ModalProps> = ({ open, onClose, onSubmit, department, defaultStartDate, defaultEndDate }) => {

    const { t } = useTranslation();

    const [mode, setMode] = useState('department');
    const [updateStatus, setUpdateStatus] = useState(0);
    const [employeesToUpdate, setEmployeesToUpdate] = useState<EmployeeEntity[]>([]);
    const role = checkTokenRole();
    const { seasonId } = useSeason();

    const fieldValidationSchema = Yup.object().shape({
        startDate: Yup.date()
            .typeError(t('employees.agreements.validation.typeError'))
            .required(t('common.forms.field.required', {
                field: t('employees.agreements.field.date'),
            }))
            .test('is-valid-date', t('common.forms.validation.invalidDate'), (value) => {
                return isValidDate(value);
            }),
        endDate: Yup.date()
            .typeError(t('employees.agreements.validation.typeError'))
            .required(t('common.forms.field.required', {
                field: t('employees.agreements.field.date'),
            }))
            .test('isValidDate', t('common.forms.validation.invalidDate'), (value) => {
                return isValidDate(value);
            })
            .test('isEndDateValid', t('employees.agreements.validation.endDateTime'), function (value) {
                return isEndDateValid(value, this.parent.startDate);
            }),
    });

    const initialValues = {
        startDate: defaultStartDate || new Date(),
        endDate: defaultEndDate || addDays(new Date(), 7),
        sendOnWhatsapp: false,
        languageOverride: '', // '' = each recipient's own language
    };

    const { values, errors, handleSubmit, setFieldValue } = useFormik({
        initialValues,
        validationSchema: fieldValidationSchema,
        onSubmit: async (values) => {
            onSubmit(values.startDate, values.endDate, mode, values.sendOnWhatsapp, values.languageOverride || undefined);
            onClose();
        }
    });

    // Helper function to format date at local midnight to avoid timezone issues
    const formatDateForBackend = (date: Date, isEndDate: boolean = false): string => {
        const localDate = new Date(date);
        localDate.setHours(0, 0, 0, 0);
        const dateString = format(localDate, 'yyyy-MM-dd');
        // For end date, use end of day to include the entire day
        return isEndDate ? `${dateString}T23:59:59.999Z` : `${dateString}T00:00:00.000Z`;
    };

    const fetchUpdateStatus = async () => {
        // -1 = no updates, 0 = to publish, 1 = to update
        if (department.id !== 0 || mode !== 'department') {
            const status = await shiftApi.isUpdate({
                params: {
                    startDate: formatDateForBackend(values.startDate, false),
                    endDate: formatDateForBackend(values.endDate, true),
                    departmentId: department && mode === 'department' ? +department.id : undefined,
                    seasonId
                }
            });

            setUpdateStatus(status.data as any);
        };
    };

    useEffect(() => {
        const fetchEmployeesToUpdate = async () => {
            if (department.id !== 0 || mode !== 'department') {
                const data = (await shiftApi.getEmployeesToPublish({
                    params: {
                        startDate: formatDateForBackend(values.startDate, false),
                        endDate: formatDateForBackend(values.endDate, true),
                        departmentId: department && mode === 'department' ? +department.id : undefined,
                        seasonId
                    }
                })).data;

                setEmployeesToUpdate(data);
            }
        };
        fetchEmployeesToUpdate();
    }, [department, values.startDate, values.endDate, open, mode]);

    useEffect(() => {
        fetchUpdateStatus();
    }, [values.startDate, values.endDate, department, open, mode]);

    useEffect(() => {
        if (open) {
            // Reset to default dates when modal opens, using provided defaults or fallback to current date
            const startDate = defaultStartDate || new Date();
            const endDate = defaultEndDate || addDays(new Date(), 7);
            setFieldValue('endDate', endDate);
            setFieldValue('startDate', startDate);
            setMode('department');
        }
    }, [open, defaultStartDate, defaultEndDate, setFieldValue]);

    return (
        <StyledAppModal
            open={open}
            handleClose={() => { onClose(); }}
        >
            <form onSubmit={handleSubmit}>
                <Grid container justifyContent='space-between' spacing={1}>
                    <Grid item xs={12}>
                        <H2> {t('shift.selectDate')} </H2>
                    </Grid>

                    <Grid item xs={12} marginBottom={2} marginTop={1}>
                        {t('shift.publishMode') + ': '}
                        <ToggleButtonGroup
                            color="primary"
                            sx={{ marginLeft: 2 }}
                            value={mode}
                            exclusive
                            onChange={(e, value) => {
                                setMode(value);
                            }}
                        >
                            <ToggleButton value={'department'}>
                                {t('department.itemName') + ': ' + department.label}
                            </ToggleButton>
                            {
                                role !== 'DEPCHIEF' &&
                                <ToggleButton value={'all'}>
                                    {t('common.all')}
                                </ToggleButton>
                            }
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid item xs={6} marginBottom={2}>
                        <CalendarInput
                            format='dd.MM.yyyy'
                            label={t('season.startDate')}
                            value={values.startDate}
                            onChange={(value) => {
                                if (value && !isNaN(+value)) {
                                    setFieldValue('startDate', value);
                                }
                            }}
                            slotProps={{
                                textField: {
                                    error: !!errors.startDate,
                                    helperText: typeof errors.startDate === 'string' ? errors.startDate : undefined,
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={6} marginBottom={2}>
                        <CalendarInput
                            format='dd.MM.yyyy'
                            label={t('season.endDate')}
                            value={values.endDate}
                            onChange={(value) => {
                                if (value && !isNaN(+value)) {
                                    setFieldValue('endDate', value);
                                }
                            }}
                            slotProps={{
                                textField: {
                                    error: !!errors.endDate,
                                    helperText: typeof errors.endDate === 'string' ? errors.endDate : undefined,
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} marginBottom={2} display="flex" justifyContent="center" alignItems="center" gap={2}>
                        <Typography>{t('shift.sendOnWhatsapp') ?? 'Send on WhatsApp'}:</Typography>
                        <WhatsAppSwitch
                            checked={values.sendOnWhatsapp}
                            onChange={async (e) => setFieldValue('sendOnWhatsapp', e.target.checked)}
                        />
                    </Grid>

                    <Grid item xs={12} marginBottom={2} display="flex" justifyContent="center">
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel>{t('shift.languageOverride')}</InputLabel>
                            <Select
                                label={t('shift.languageOverride')}
                                value={values.languageOverride}
                                onChange={async (e) => setFieldValue('languageOverride', e.target.value)}
                            >
                                <MenuItem value="">{t('shift.languageAuto')}</MenuItem>
                                <MenuItem value="IT">Italiano</MenuItem>
                                <MenuItem value="EN">English</MenuItem>
                                <MenuItem value="DE">Deutsch</MenuItem>
                                <MenuItem value="FR">Français</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {
                        updateStatus === -1 && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    {t('shift.noUpdates')}
                                </Alert>
                            </Grid>
                        )
                    }
                    <Grid container maxHeight={'50vh'} overflow={'auto'}>
                        {
                            employeesToUpdate.map((e) => {
                                return (
                                    <Grid item xs={12} key={e.id}>
                                        <ToBePublishedAccordion
                                            employee={e}
                                            modifiedShifts={(e as any).shiftAssignments}
                                        />
                                    </Grid>
                                );
                            })
                        }
                    </Grid>

                    <Grid item xs={5}>
                        <Button fullWidth size='small' variant='outlined' onClick={() => { onClose(); }}>
                            {t('common.forms.button.cancel')}
                        </Button>
                    </Grid>
                    <Grid item xs={5}>
                        <Button fullWidth size='small' type="submit" variant='contained' disabled={updateStatus === -1}>
                            {updateStatus === 1 ? t('shift.update') : t('shift.publish')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </StyledAppModal>
    );
};

export default PublishShiftsDatePicker;